import { defineStore } from "pinia";
import {
  createDepartment as createDepartmentRequest,
  deleteDepartmentById,
  getDepartmentsInfo,
  updateDepartmentById,
} from "@/api/getInfo";
import { notify } from "@/utils/nativeFeedback";
import {
  createPersistenceConfig,
  readCache,
  writeCache,
  clearCache,
  isCacheAvailable,
  isCacheExpiring,
  getCacheInfo,
  type CacheInfo,
} from "@/utils/cache";

// ========== 持久化配置 ==========
const PERSISTENCE_CONFIG = createPersistenceConfig("departments");

export type DepartmentType = {
  achievements: { achievement: string; level: string; sortOrder: number }[];
  category: string;
  contact: string;
  descimp: string;
  descmain: string;
  description: string;
  director: string;
  features: { feature: string; sortOrder: number }[];
  group: string;
  id: number;
  image: string;
  introduce: string;
  level: string;
  name: string;
  outpatients: string;
  projects: { project: string; sortOrder: number }[];
};

interface DepartmentsState {
  departmentsList: DepartmentType[];
}

export const useDepartmentsStore = defineStore("departments", {
  state: (): DepartmentsState => ({
    departmentsList: [],
  }),
  getters: {
    getDepartmentsList: (state) => state.departmentsList,
    getKeyDepartments: (state) =>
      state.departmentsList.filter(
        (dept) => dept.level !== null && dept.level !== "",
      ),
    getHomeDepartments: (state) =>
      state.departmentsList.filter(
        (dept) => dept.descmain !== null && dept.descmain !== "",
      ),
  },
  actions: {
    async fetchDepartmentsList(
      forceRefresh = false,
      silentRefresh = false,
    ): Promise<void> {
      // 1. 检查内存缓存
      if (this.departmentsList.length > 0 && !forceRefresh) {
        return;
      }

      // 2. 检查 localStorage 缓存
      if (!forceRefresh) {
        const cachedData = readCache<DepartmentType[]>(PERSISTENCE_CONFIG);

        if (cachedData && Array.isArray(cachedData)) {
          this.departmentsList = cachedData;

          // 静默刷新：缓存即将过期时后台异步刷新
          if (silentRefresh && isCacheExpiring(PERSISTENCE_CONFIG)) {
            this.refreshInBackground();
          }

          return;
        }
      }

      // 3. 请求接口
      try {
        const departments = await getDepartmentsInfo();
        const data = departments.data;

        if (!Array.isArray(data)) {
          throw new Error("接口返回数据格式异常");
        }

        this.departmentsList = data;
        writeCache(PERSISTENCE_CONFIG, data);
      } catch (error) {
        // 降级策略
        if (isCacheAvailable(PERSISTENCE_CONFIG)) {
          const fallbackData = readCache<DepartmentType[]>(PERSISTENCE_CONFIG);
          if (fallbackData && Array.isArray(fallbackData)) {
            this.departmentsList = fallbackData;
            return;
          }
        }

        if (this.departmentsList.length === 0) {
          throw error;
        }
      }
    },

    async refreshInBackground(): Promise<void> {
      try {
        const departments = await getDepartmentsInfo();
        const data = departments.data;

        if (Array.isArray(data)) {
          this.departmentsList = data;
          writeCache(PERSISTENCE_CONFIG, data);
        }
      } catch (error) {}
    },

    async refreshDepartments(): Promise<void> {
      return this.fetchDepartmentsList(true, false);
    },

    clearCache(): void {
      this.departmentsList = [];
      clearCache(PERSISTENCE_CONFIG);
    },

    getCacheInfo(): CacheInfo {
      return getCacheInfo(PERSISTENCE_CONFIG);
    },

    isValidDepartmentArray(data: unknown): data is DepartmentType[] {
      return (
        Array.isArray(data) &&
        data.every(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            "id" in item &&
            typeof (item as DepartmentType).id === "number",
        )
      );
    },

    updateDepartment(updatedDepartment: DepartmentType): void {
      const index = this.departmentsList.findIndex(
        (d) => d.id === updatedDepartment.id,
      );
      if (index !== -1) {
        this.departmentsList[index] = {
          ...this.departmentsList[index],
          ...updatedDepartment,
        };
        writeCache(PERSISTENCE_CONFIG, this.departmentsList);
      }
    },

    async createDepartment(data: Partial<DepartmentType>): Promise<boolean> {
      try {
        const response = await createDepartmentRequest(data);
        if (response?.code !== 200) {
          notify(response?.msg || "新增科室失败", "error");
          return false;
        }
        await this.refreshDepartments();
        notify("新增成功", "success");
        return true;
      } catch (error) {
        console.error("新增科室失败:", error);
        notify("新增科室失败", "error");
        return false;
      }
    },

    async updateDepartmentById(
      id: number,
      data: Partial<DepartmentType>,
    ): Promise<boolean> {
      try {
        const response = await updateDepartmentById(id, data);
        if (response?.code !== 200) {
          notify(response?.msg || "更新科室失败", "error");
          return false;
        }
        const current = this.departmentsList.find((department) => department.id === id);
        if (current) this.updateDepartment({ ...current, ...data, id });
        notify("更新成功", "success");
        return true;
      } catch (error) {
        console.error("更新科室失败:", error);
        notify("更新科室失败", "error");
        return false;
      }
    },

    async deleteDepartment(id: number): Promise<boolean> {
      try {
        await deleteDepartmentById(id);
        this.departmentsList = this.departmentsList.filter(
          (department) => department.id !== id,
        );
        writeCache(PERSISTENCE_CONFIG, this.departmentsList);
        notify("删除成功", "success");
        return true;
      } catch (error) {
        console.error("删除科室失败:", error);
        notify("删除科室失败", "error");
        return false;
      }
    },
  },
});
